/**
 * SCK-607 — first-time getting-started modal (cross-cutting from
 * SCK-462).
 *
 * Fires ONCE per user after NDA acceptance + first sign-in. Content
 * varies by the caller's most-recent redeemed invitation kind
 * (SOLO / ACCOUNT_MEMBER / PROJECT_MEMBER / BETA / ACCOUNT_ADMIN /
 * null-generic). A "don't show me again" checkbox persists the
 * dismissal to coord (per-user, cross-device via
 * PUT /api/me/preferences) AND to localStorage (fast-path so the
 * modal doesn't flash on the next mount before the server round-trip
 * completes).
 *
 * Trigger order (in Main.js after WelcomeNamingModal):
 *   1. localStorage['c2a:gettingStarted:dismissed'] === 'true' → skip
 *   2. GET /api/me/preferences — gettingStartedModal==='dismissed' → skip
 *   3. GET /api/me/invitation-kind → open with the right kind variant
 *
 * A GET failure on either endpoint means we silently DO NOT show the
 * modal (fail closed) — first-time UX is a nice-to-have, and popping
 * a modal on a partially-broken control plane would confuse more
 * than help.
 *
 * See coord's MePreferencesController + MeInvitationKindController
 * for the server contract (SCK-607 coord PR).
 */

import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon, CheckCircleOutline } from "@mui/icons-material";

import { API_URL } from "../helpers/constants";
import { getToken } from "../helpers/tokenStorage";

export const DISMISSED_STORAGE_KEY = "c2a:gettingStarted:dismissed";

// Content variants keyed by invitation kind. Kept as pure data so the
// mapping is easy to eyeball + easy to test — a variant-lookup test
// asserts the right title/bullets/CTA come back for each kind.
//
// title  — hero title (rendered under the "Welcome to Clue2App" header)
// intro  — one-sentence framing, receives {accountName, projectName,
//          featureName} substitutions where available
// steps  — 3-4 short "next step" bullets
// cta    — primary CTA button label + destination path (SPA route)
export const VARIANTS = {
  SOLO: {
    title: (ctx) =>
      ctx.accountName
        ? `Your new account "${ctx.accountName}" is ready`
        : "Your new account is ready",
    intro:
      "You're the admin of a fresh trial account. Deploy your first app, invite teammates when you're ready, and manage billing later from Account Settings.",
    steps: [
      "Create a new app from a template or your own Git repo",
      "Watch your first build stream in the Builds tab",
      "Add environment variables and secrets under Configs",
      "Attach a custom domain when you're ready to go live",
    ],
    cta: { label: "Deploy your first app", to: "/" },
  },
  ACCOUNT_ADMIN: {
    title: (ctx) =>
      ctx.accountName
        ? `You're the admin of "${ctx.accountName}"`
        : "Your new account is ready",
    intro:
      "You have full admin rights on this account. Set up projects, invite teammates, and manage billing from Account Settings.",
    steps: [
      "Invite teammates from Access → Members",
      "Create your first project and grant per-project roles",
      "Review billing details in Account Settings",
      "Deploy your first app from the Overview tab",
    ],
    cta: { label: "Manage account", to: "/account" },
  },
  ACCOUNT_MEMBER: {
    title: (ctx) =>
      ctx.accountName
        ? `You've joined "${ctx.accountName}"`
        : "You've joined the account",
    intro:
      "You're a member of this account. What you can do depends on the roles your admin has granted you — head to Members to see who else is here.",
    steps: [
      "See your teammates on Access → Members",
      "Open a project to see its apps and builds",
      "Ask an admin to bump your role when you need more access",
    ],
    cta: { label: "Meet your team", to: "/access/members" },
  },
  PROJECT_MEMBER: {
    title: (ctx) =>
      ctx.projectName
        ? `You've been granted access to "${ctx.projectName}"`
        : "You've been granted project access",
    intro:
      "You have access to a single project. Switch to it from the project picker in the top bar to see its apps, builds, and secrets.",
    steps: [
      "Switch to the project from the top-bar project picker",
      "Browse its apps under the Overview tab",
      "Open Builds to watch a deploy stream",
    ],
    cta: { label: "Go to overview", to: "/" },
  },
  BETA: {
    title: (ctx) =>
      ctx.featureName
        ? `You now have early access to "${ctx.featureName}"`
        : "You now have early access to a preview feature",
    intro:
      "This is a preview capability — expect rough edges, and please send feedback the moment something feels off.",
    steps: [
      "Try the feature from wherever it appears in the console",
      "Read the preview notes in the release notes",
      "Report anything unexpected — feedback shapes the GA cut",
    ],
    cta: { label: "See release notes", to: "/" },
  },
  // Fallback for null / unknown kind. Kept deliberately generic —
  // never mention "your account" specifically since we don't know how
  // this user was provisioned.
  GENERIC: {
    title: () => "Welcome to Clue2App",
    intro:
      "Deploy apps, manage projects, and invite teammates from one place. Here's how to get started.",
    steps: [
      "Deploy an app from a template or your Git repo",
      "Add environment variables under Configs",
      "Watch builds stream in the Builds tab",
    ],
    cta: { label: "Get started", to: "/" },
  },
};

export function pickVariant(kind) {
  if (kind && VARIANTS[kind]) return VARIANTS[kind];
  return VARIANTS.GENERIC;
}

const safeLocalStorageRead = () => {
  try {
    return typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem(DISMISSED_STORAGE_KEY)
      : null;
  } catch {
    return null;
  }
};

const safeLocalStorageWrite = (val) => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, val);
    }
  } catch {
    /* ignore — some browsers throw on private mode */
  }
};

export const safeLocalStorageClear = () => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(DISMISSED_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
};

/**
 * The modal component itself. Callers render it unconditionally at
 * the layout level; it self-detects whether to open. Renders nothing
 * until either localStorage or the server confirms the modal should
 * fire (avoids a flash-open-then-close on a fast dismissal path).
 *
 * `onNavigate` is an optional injection for tests + the CTA button —
 * defaults to a plain window.location.assign so this component can
 * be dropped in without a Router context (e.g. Storybook, unit tests).
 */
export default function GettingStartedModal({ onNavigate } = {}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState({});
  const [variant, setVariant] = useState(VARIANTS.GENERIC);
  const [dontShowAgain, setDontShowAgain] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const token = getToken();
    if (!token) return () => { cancelled = true; };

    // Fast-path — the localStorage flag is per-device but cheap.
    if (safeLocalStorageRead() === "true") {
      return () => { cancelled = true; };
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    (async () => {
      try {
        // Fail-closed: any error path leaves the modal closed. First-
        // time UX must never flash for someone who's already dismissed
        // it (server said so) or block on a coord hiccup.
        const prefsRes = await fetch(`${API_URL}/me/preferences`, { headers });
        if (!prefsRes.ok) return;
        const prefs = await prefsRes.json();
        if (prefs?.preferences?.gettingStartedModal === "dismissed") {
          // Server said dismissed — cache locally so we skip the
          // network round-trip next mount.
          safeLocalStorageWrite("true");
          return;
        }

        const kindRes = await fetch(`${API_URL}/me/invitation-kind`, { headers });
        if (!kindRes.ok) return;
        const kindPayload = await kindRes.json();
        if (cancelled) return;
        setCtx({
          accountName: kindPayload?.accountName,
          projectName: kindPayload?.projectName,
          featureName: kindPayload?.featureName,
        });
        setVariant(pickVariant(kindPayload?.kind));
        setOpen(true);
      } catch {
        /* fail-closed */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const persistDismissed = async () => {
    // Local first so the next mount skips even if the server call
    // fails or is slow.
    safeLocalStorageWrite("true");
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${API_URL}/me/preferences`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ gettingStartedModal: "dismissed" }),
      });
    } catch {
      /* localStorage still stops the next open; server retry is
         out of scope for this slice. */
    }
  };

  const handleClose = async () => {
    setOpen(false);
    if (dontShowAgain) {
      await persistDismissed();
    }
  };

  const handleCta = async () => {
    // Treat clicking the CTA the same as dismissing — if the user is
    // moving forward, they don't need the modal to reappear. Then
    // navigate.
    await persistDismissed();
    setOpen(false);
    const dest = variant?.cta?.to || "/";
    if (typeof onNavigate === "function") {
      onNavigate(dest);
    } else if (typeof window !== "undefined") {
      window.location.assign(dest);
    }
  };

  if (!open) return null;

  const heroTitle =
    typeof variant.title === "function" ? variant.title(ctx) : variant.title;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="getting-started-modal"
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: isDarkMode
            ? "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)"
            : "linear-gradient(180deg, #ffffff 0%, #fff7ed 100%)",
          color: isDarkMode ? "#e2e8f0" : "#0f172a",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          pr: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
          >
            Welcome to Clue2App
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mt: 0.5 }}
            data-testid="getting-started-title"
          >
            {heroTitle}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          size="small"
          data-testid="getting-started-close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body1"
          sx={{ mb: 2, color: isDarkMode ? "#cbd5e1" : "#334155" }}
          data-testid="getting-started-intro"
        >
          {variant.intro}
        </Typography>
        <List dense data-testid="getting-started-steps">
          {variant.steps.map((step) => (
            <ListItem key={step} disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleOutline
                  fontSize="small"
                  sx={{ color: "#EA580C" }}
                />
              </ListItemIcon>
              <ListItemText primary={step} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              inputProps={{ "data-testid": "getting-started-dontshow" }}
            />
          }
          label="Don't show me again"
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={handleClose} data-testid="getting-started-later">
            Maybe later
          </Button>
          <Button
            onClick={handleCta}
            variant="contained"
            sx={{
              background: "#EA580C",
              "&:hover": { background: "#C2410C" },
            }}
            data-testid="getting-started-cta"
          >
            {variant.cta.label}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
